import { FC, useState } from "react";
import { Stack, Typography } from "@mui/material";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { yupResolver } from "@hookform/resolvers/yup";
import { Form } from "@/components/elements/Form";
import { MenuNavigate } from "@/components/elements/MenuNavigate";
import { Button } from "@/components/elements/Button";
import { CompanyFooterContainer } from "@/features/company/components/CompanyFooterContainer";
import { CompanyUserOrgInfo } from "@/features/company/components/company-user/CompanyUserOrgInfo";
import { CompanyUserFormInfo } from "@/features/company/components/company-user/CompanyUserFormInfo";
import { useGetCompanyUserSchema } from "@/features/company/hooks/useGetCompanyUserSchema";
import { TCompanyUserForm } from "@/features/company/types/CompanyUserForm";
import { TGetCompanyDto } from "@/api/services/main/company/dtos/get-company.dto";
import { createCompanyUserApi } from "@/api/services/main/company-user";
import { getDynamicRoute } from "@/utils/url";
import { AppRoutes } from "@/routes/config";
import { showError } from "@/utils/error";
import { toast } from "@/utils/toast";
import { API_SUCCESS_MESSAGE } from "@/constants/common";
import { text } from "@/theme/colors";
import { IconResend } from "@/assets/icons";
import { USER_ACTIVE_STATUS } from "@/types/enum";
import { CompanyUserPermissions } from "@/features/company/components/company-user/CompanyUserPermissions";
import { getDefaultPermissions, pickSubmittablePermissions } from "@/features/company/utiles/permission";

type TCompanyUserCreateContentProps = {
  companyData: TGetCompanyDto;
};

const INIT_FORM: TCompanyUserForm = {
  fullName: "",
  email: "",
  activeStatus: USER_ACTIVE_STATUS.ACTIVE,
  permissions: getDefaultPermissions(),
};


export const CompanyUserCreateContent: FC<TCompanyUserCreateContentProps> = ({ companyData }) => {
  const navigate = useNavigate();
  const { companyId } = useParams();
  const { companyUserSchema } = useGetCompanyUserSchema();
  const [isDisableButton, setIsDisableButton] = useState(false);

  const methods = useForm<TCompanyUserForm>({
    defaultValues: INIT_FORM,
    resolver: yupResolver(companyUserSchema),
    mode: "all",
    reValidateMode: "onBlur",
  });

  const { handleSubmit: _handleSubmit } = methods;

  const onSubmit = async (values: TCompanyUserForm) => {
    if (!companyId) return;
    setIsDisableButton(true);
    try {
      const rs = await createCompanyUserApi(companyId, {
        ...values,
        permissions: pickSubmittablePermissions(values.permissions),
      });
      toast.success(rs.message ?? API_SUCCESS_MESSAGE);
      navigate(getDynamicRoute(AppRoutes.companyDetail, { companyId }));
    } catch (e) {
      showError(e);
    } finally {
      setIsDisableButton(false);
    }
  };

  return (
    <>
      <Stack mb={4}>
        <Typography variant="head24Bold">{companyData.companyName}のユーザー追加</Typography>
      </Stack>
      <Stack mt={3}>
        <Form methods={methods} onSubmit={onSubmit} schema={companyUserSchema} initialValues={INIT_FORM}>
          <MenuNavigate
            contents={[
              { title: "基本情報", content: <CompanyUserOrgInfo companyData={companyData} /> },
              { title: "ユーザー情報", content: <CompanyUserFormInfo isCreateMode /> },
              { title: "アクセス権限", content: <CompanyUserPermissions /> },
            ]}
            contentWidth="900px"
            contentAffix={
              <CompanyFooterContainer>
                <Stack direction="row" gap={3}>
                  <Button disabled={isDisableButton} size="sm" type="submit" startIcon={<IconResend />}>
                    メールを送信
                  </Button>
                </Stack>
              </CompanyFooterContainer>
            }
          />
        </Form>
      </Stack>
    </>
  );
};
